import storeManager from '../../store/StoreManager';
import settingsReader from '../../store/readHelpers/SettingsReader';
import cacheManager, { cacheContexts } from './cacheManager';
import { cacheResultTypes, cacheStatusTypes } from '../../actions/CacheStatusActions';
import loadProgressHandler from './loadProgressHandler';
import sessionReader from '../../store/readHelpers/SessionReader';

const resourceCacheService = {
    async cacheParentChildResourcesInBackground() {
        loadProgressHandler.startComputingProgress();

        try {
            const { parentTest: { readinessCheckContentRevisionId } } = storeManager.getState();
            await this.cacheReadinessCheckResources(readinessCheckContentRevisionId);
            await this.cacheInfoDocsResources();
            await this.cacheChildTestResources();
        } catch (e) {
            console.error('Resource caching did not complete!', e);
        }

        loadProgressHandler.stopComputingProgress();
    },

    async cacheReadinessCheckResources(readinessCheckContentRevisionId) {
        const { pendingAssessments } = storeManager.getState();
        const { assessmentItem, settings } = pendingAssessments[readinessCheckContentRevisionId] || {};

        if (assessmentItem && settingsReader.shouldCacheResourcesBeforeTestStart(settings)) {
            const isCached = await cacheManager.isReadinessCheckQuestionSetAlreadyCached(readinessCheckContentRevisionId);
            if (!isCached) {
                await cacheManager.makeRoomForANewReadinessCheckCache();
            }
            return cacheManager.cacheQuestionSetMedia(JSON.parse(JSON.stringify(assessmentItem)), cacheContexts.READINESS_CHECK);
        } else if (assessmentItem) {
            cacheManager.reportCacheStatus(readinessCheckContentRevisionId, cacheStatusTypes.DONE, cacheResultTypes.SKIPPED);
        }

        return undefined;
    },

    async cacheChildTestResources() {
        const { assessment, parentTest, pendingAssessments, settings } = storeManager.getState();
        const itemsToCache = parentTest.childTests
            .filter(childTest => childTest.assessmentRunId in pendingAssessments || assessment.assessmentRunId === childTest.assessmentRunId)
            .map((childTest) => {
                let data;
                if (childTest.assessmentRunId in pendingAssessments) {
                    data = pendingAssessments[childTest.assessmentRunId];
                } else {
                    data = { assessmentItem: assessment, settings };
                }
                return JSON.parse(JSON.stringify(data));
            });

        // Cache all assessmentItems if at least one has caching required.
        const shouldCache = itemsToCache.some(data => settingsReader.shouldCacheResourcesBeforeTestStart(data.settings));

        if (!shouldCache) {
            return false;
        }

        loadProgressHandler.startReportingMonitorProgress();
        if (sessionReader.isResumed()) {
            loadProgressHandler.reportMonitorLoadStatusForOneTest(parentTest.assessment.assessmentRunId, 0);
        }

        let allGood = true;
        for (let i = 0; i < itemsToCache.length; i++) {
            const { assessmentItem } = itemsToCache[i];
            try {
                if (sessionReader.isResumed()) {
                    loadProgressHandler.reportMonitorLoadStatusForOneTest(assessmentItem.assessmentRunId, 0);
                }
                // eslint-disable-next-line no-await-in-loop
                await cacheManager.cacheQuestionSetMedia(assessmentItem, cacheContexts.TEST);
            } catch (error) {
                allGood = false;
                break;
            }
            loadProgressHandler.reportMonitorLoadStatusForOneTest(assessmentItem.assessmentRunId, 100);
        }
        loadProgressHandler.stopReportingMonitorProgress();
        if (allGood) {
            loadProgressHandler.reportMonitorLoadStatusForOneTest(parentTest.assessment.assessmentRunId, 100);
        }
        return allGood;
    },

    async cacheInfoDocsResources() {
        const { parentTest } = storeManager.getState();
        const itemsToCache = parentTest.childTests
            .filter(childTest => childTest.documentModel !== undefined);

        return cacheManager.cacheChildTestInfoDocs(itemsToCache);
    },
};

export default resourceCacheService;
